import { FC, KeyboardEvent, useState } from "react";
import {
  Box,
  InputBase,
  Typography,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import { useNavigate } from "react-router";

import { useChatStore } from "../../../../hooks/useChatStore";
import { useOrgLinkBranding } from "../../../../contexts/AuthContext";
import { GradientIconButton } from "../../core/GradientButton";

const SUGGESTED_QUESTIONS = [
  "Tell me what you can do for me",
  "Show me a list of my contracts",
  "What payer policies do you have access to?",
];

const SparkleIcon: FC = () => {
  const theme = useTheme();
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M7 0.5c.3 2.6 1.4 3.7 4 4-2.6.3-3.7 1.4-4 4-.3-2.6-1.4-3.7-4-4 2.6-.3 3.7-1.4 4-4Z"
        fill={theme.palette.custom.violetMain}
      />
    </svg>
  );
};

const SendArrowIcon: FC = () => {
  const theme = useTheme();
  return (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <path
        d="M2.917 7h8.166M7.583 3.5 11.083 7l-3.5 3.5"
        stroke={theme.palette.common.white}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ExtendedHomeAskReveSection: FC = observer(() => {
  const navigate = useNavigate();
  const chatStore = useChatStore();
  const { askReveLabel } = useOrgLinkBranding();
  const [question, setQuestion] = useState("");

  const handleSend = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || chatStore.isQuestioning) return;

    setQuestion("");
    // askQuestion attaches whatever claim/policy/contract context is left on
    // the store (claimIdToPreview, channel, claimsStore.claimToPreview, ...)
    // to the first message. Home always starts a brand-new chat, so clear
    // that context first rather than relying on it having been reset by
    // however the user navigated here (e.g. browser back skips the header
    // nav's reset-on-click).
    chatStore.reset();
    const redirectId = await chatStore.askQuestion(
      trimmed,
      undefined,
      undefined,
      true,
    );
    if (redirectId) {
      navigate(`/channels/${redirectId}`);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend(question);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
        padding: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "15px",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: "text.primary" }}>
        {askReveLabel}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 999,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover",
          pl: 2,
          pr: 0.5,
          py: 0.5,
        }}
      >
        <InputBase
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={chatStore.isQuestioning}
          placeholder="Ask a question..."
          sx={{ flex: 1, minWidth: 0, fontSize: 13 }}
        />
        <GradientIconButton
          onClick={() => handleSend(question)}
          disabled={!question.trim() || chatStore.isQuestioning}
          aria-label="Send"
          sx={{ height: 36, width: 36 }}
        >
          <SendArrowIcon />
        </GradientIconButton>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {SUGGESTED_QUESTIONS.map((suggestion) => (
          <Box
            key={suggestion}
            component="button"
            type="button"
            onClick={() => handleSend(suggestion)}
            disabled={chatStore.isQuestioning}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              border: "none",
              borderRadius: "10px",
              bgcolor: "action.hover",
              px: 1.5,
              py: 1,
              textAlign: "left",
              cursor: "pointer",
              font: "inherit",
              "&:hover": { bgcolor: "custom.violetLight" },
              "&:disabled": { cursor: "default", opacity: 0.6 },
            }}
          >
            <SparkleIcon />
            <Typography sx={{ fontSize: 13, color: "text.primary" }}>
              {suggestion}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

export default ExtendedHomeAskReveSection;
